import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { BlockWrapper, defineBlock } from "@smartfire/js-block-engine";
import clsx from "clsx";
import he from "he";
import { useTranslation } from "react-i18next";
import InfoSelector from "./InfoSelector";
import Button from "../../button/block";
import DisplayBrand from "./DisplayBrand";
import Gallery from "./Gallery";
import Sizes from "./Sizes";
import SocialShare from "./SocialShare";
import style from "./block.module.scss";
import { ReactComponent as YoutubeVideo } from "../../assets/youtube-video.svg";
import { ReactComponent as CloseIcon } from "../../assets/close.svg";

const Block = ({
  brand,
  brandProductsUrl,
  product,
  availableSizes,
  storeLocatorLink,
  buyWithAmazon,
}) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: " + style.mobileBreakpoint + ")");
  const images = product.images;
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  // React i18next
  const { t } = useTranslation();

  const handleOpenVideo = (event) => {
    event.preventDefault();
    setIsVideoOpen(true);
  };

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
    setIsIframeLoaded(false);
  };

  const isYoutubeVideoId = (id) => {
    const regex = /http|:\/\/|youtu/;
    if (!id || id.match(regex)) {
      return false;
    }
    return true;
  };

  // Get update C2B object
  let c2bWidget = window.c2bWidget;

  // Update C2B object when component is rendered
  useEffect(() => {
    if (c2bWidget) {
      c2bWidget.init();
    }
  }, [c2bWidget]);

  const ContentTop = () => (
    <div>
      <DisplayBrand brand={brand} brandProductsUrl={brandProductsUrl} />
      <h1 className={style.title}>{he.decode(product.title)}</h1>
    </div>
  );

  return (
    <BlockWrapper.Div className={style.block}>
      <div className={style.presentation}>
        {isMobile && <ContentTop />}

        <div className={style.gallery}>
          <Gallery images={images} />

          {product.youtube && isYoutubeVideoId(product.youtube) && (
            <button onClick={handleOpenVideo} className={style.youtubeButton}>
              <YoutubeVideo />

              <span className={style.btnText}>{t("Watch product video")}</span>
            </button>
          )}
        </div>

        {isVideoOpen && (
          <div className={style.videoContainerPopup}>
            <div
              className={clsx(
                style.videoPopup,
                isIframeLoaded && style.showVideoPopup
              )}
            >
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${product.youtube}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={() => setIsIframeLoaded(true)}
              />

              <button onClick={handleCloseVideo} className={style.closeButton}>
                <CloseIcon />
              </button>
            </div>
          </div>
        )}

        <div className={style.content}>
          {!isMobile && <ContentTop />}

          {buyWithAmazon ? (
            <div
              className={clsx(
                style.buttonContainer,
                buyWithAmazon && style.buyWithAmazon,
                buyWithAmazon &&
                  (storeLocatorLink || product.buyOnlineLink.link) &&
                  style.withMargin
              )}
            >
              {storeLocatorLink && (
                <Button
                  className={style.button}
                  tagName="a"
                  text={t("Find your shop")}
                  link={storeLocatorLink}
                  color={style.darkGreen}
                  colorHover={style.darkerGreen}
                  showInspector={false}
                />
              )}
              {product.buyOnlineLink.link && (
                <Button
                  className={style.button}
                  tagName="a"
                  text={product.buyOnlineLink.text || t("Buy online")}
                  link={product.buyOnlineLink.link}
                  target="_blank"
                  showInspector={false}
                />
              )}
            </div>
          ) : product.enableBuyOnline ? (
            <div
              className={clsx(
                style.buttonContainer,
                style.buyWithAmazon,
                (storeLocatorLink || product.buyOnlineLink.link) &&
                  style.withMargin
              )}
            >
              {product.buyOnlineLink.link && (
                <Button
                  className={style.button}
                  tagName="a"
                  text={product.buyOnlineLink.text || t("Buy online")}
                  link={product.buyOnlineLink.link}
                  target="_blank"
                  showInspector={false}
                />
              )}
              <div
                className={clsx(style.buttonEan, style.buttonEan50)}
                data-widget-element
                data-widget-ean={product.codeEan}
              >
                <span>{t("Buy online")}</span>
              </div>
            </div>
          ) : (
            <div
              className={clsx(
                style.buttonContainer,
                buyWithAmazon &&
                  (storeLocatorLink || product.buyOnlineLink.link) &&
                  style.withMargin
              )}
              data-widget-element
              data-widget-ean={product.codeEan}
            >
              <div className={style.buttonEan}>{t("Buy online")}</div>
            </div>
          )}

          <div
            className={style.description}
            dangerouslySetInnerHTML={{ __html: product.description || "" }}
          />

          <div
            className={style.benefits}
            dangerouslySetInnerHTML={{ __html: product.benefits || "" }}
          />

          <div className={style.sizes}>
            <Sizes currentSize={product.size} sizes={availableSizes} />
          </div>

          <div className={style.share}>
            <SocialShare imageUrl={images.length > 0 && images[0]?.url} />
          </div>
        </div>
      </div>

      <div className={style.infoSelector}>
        <InfoSelector product={product} />
      </div>
    </BlockWrapper.Div>
  );
};

export default defineBlock({
  Block,
});
